import React, { useState, useRef, useEffect } from 'react'
import Typography from '../../../../components/common/Typography'
import MiniHeader from '../../../../components/layout/MiniHeader'
import Pagination from '../../../../components/common/Pagination/Pagination'
import GridTable from '../../../../components/common/GridTable/GridTable'
import DarkTabs from '../../../../components/common/Tabs/DarkTabs'
import Button from '../../../../components/common/Button'
import { LuPencil } from 'react-icons/lu'
import { FiPlusCircle } from 'react-icons/fi'
import { IoMdArrowDropdown } from 'react-icons/io'
import { CgAttachment } from 'react-icons/cg'
import { RxLetterCaseCapitalize } from 'react-icons/rx'
import { IoArrowBack } from 'react-icons/io5'
import MessageReply from '../MessageReply/MessagReply'
import useMessage from '../../hooks/useMessage'

const tabs = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'sent', label: 'Sent' },
  { id: 'drafts', label: 'Drafts' },
]

const columns = [
  { colname: 'Sender', colSpan: 'col-span-2' },
  { colname: 'Message', colSpan: 'col-span-2' },
  { colname: 'Date', colStyle: 'flex justify-end' },
]

const MessageList = () => {
  const [activeTab, setActiveTab] = useState('inbox')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [composeOpen, setComposeOpen] = useState(false)
  const [replyOpen, setReplyOpen] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [viewingMessage, setViewingMessage] = useState(null)
  const [recipient, setRecipient] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [attachedFiles, setAttachedFiles] = useState([])
  const [users, setUsers] = useState({}) // Cache for user data
  const [loadingUsers, setLoadingUsers] = useState(false)

  const fileInputRef = useRef(null)
  const textareaRef = useRef(null)

  // Use the custom hook
  const {
    inbox,
    sent,
    drafts,
    unreadCount,
    loading,
    error,
    fetchInbox,
    fetchSent,
    fetchDrafts,
    sendMessage,
  } = useMessage({ fetch: activeTab })

  // Function to fetch user details by ID or email
  const fetchUserDetails = async (identifier) => {
    if (users[identifier]) {
      return users[identifier]
    }

    try {
      setLoadingUsers(true)
      // Replace this with your actual API endpoint
      const response = await fetch(`/api/users/${identifier}`)
      if (response.ok) {
        const userData = await response.json()
        setUsers(prev => ({
          ...prev,
          [identifier]: userData
        }))
        return userData
      }
    } catch (error) {
      console.error('Error fetching user details:', error)
    } finally {
      setLoadingUsers(false)
    }
    
    // Return fallback data if API call fails
    return {
      id: identifier,
      fullName: identifier.includes('@') ? identifier.split('@')[0] : identifier,
      email: identifier.includes('@') ? identifier : `${identifier}@example.com`
    }
  }

  // Function to get display name for a user
  const getUserDisplayName = (identifier, userData = null) => {
    if (userData) {
      return userData.fullName || userData.firstName + ' ' + userData.lastName || userData.name || identifier
    }
    
    const cachedUser = users[identifier]
    if (cachedUser) {
      return cachedUser.fullName || cachedUser.firstName + ' ' + cachedUser.lastName || cachedUser.name || identifier
    }
    
    // If no user data available, return a formatted version of the identifier
    if (identifier && identifier.includes('@')) {
      return identifier.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
    
    return identifier || 'Unknown User'
  }

  // Function to load user details for all messages
  const loadUserDetailsForMessages = async (messages) => {
    if (!messages || messages.length === 0) return

    const userIds = new Set()
    
    messages.forEach(msg => {
      if (msg.senderId) userIds.add(msg.senderId)
      if (msg.senderEmail) userIds.add(msg.senderEmail)
      if (msg.recipientId) userIds.add(msg.recipientId)
      if (msg.recipientEmail) userIds.add(msg.recipientEmail)
      if (msg.fromId) userIds.add(msg.fromId)
      if (msg.toId) userIds.add(msg.toId)
    })

    const promises = Array.from(userIds).map(id => fetchUserDetails(id))
    await Promise.all(promises)
  }

  // Fetch messages on tab change
  useEffect(() => {
    if (activeTab === 'inbox') fetchInbox()
    if (activeTab === 'sent') fetchSent()
    if (activeTab === 'drafts') fetchDrafts()
  }, [activeTab])

  // Load user details when messages are loaded
  useEffect(() => {
    const messages = activeTab === 'inbox' ? (inbox?.messages || inbox) :
                    activeTab === 'sent' ? (sent?.messages || sent) :
                    (drafts?.messages || drafts)
    
    if (Array.isArray(messages) && messages.length > 0) {
      loadUserDetailsForMessages(messages)
    }
  }, [inbox, sent, drafts, activeTab])

  // Log messages to console for debugging
  useEffect(() => {
    const inboxMessages = inbox?.messages || inbox
    const sentMessages = sent?.messages || sent
    const draftMessages = drafts?.messages || drafts

    console.log('Inbox:', inbox)
    console.log('Inbox Messages:', inboxMessages)
    console.log('Sent:', sent)
    console.log('Sent Messages:', sentMessages)
    console.log('Drafts:', drafts)
    console.log('Draft Messages:', draftMessages)
    console.log('Loaded Users:', users)
  }, [inbox, sent, drafts, users])

  const handleRefresh = () => {
    setCurrentPage(1)
    setViewingMessage(null)
    if (activeTab === 'inbox') fetchInbox()
    if (activeTab === 'sent') fetchSent()
    if (activeTab === 'drafts') fetchDrafts()
  }

  const handleCompose = () => {
    setComposeOpen(true)
    setRecipient('')
    setRecipientName('')
    setSubject('')
    setMessage('')
    setAttachedFiles([])
  }

  const handleCloseCompose = () => {
    setComposeOpen(false)
  }

  const handleMessageClick = (msg) => {
    setViewingMessage(msg)
  }

  const handleBackToList = () => {
    setViewingMessage(null)
    setReplyOpen(false)
    setSelectedMessage(null)
  }

  const handleReply = (msg) => {
    setSelectedMessage(msg)
    const senderEmail = msg.senderEmail || msg.fromEmail || msg.sender || msg.from || ''
    const senderId = msg.senderId || msg.fromId
    
    setRecipient(senderEmail)
    setRecipientName(getUserDisplayName(senderId || senderEmail))
    setSubject(msg.subject ? `Re: ${msg.subject}` : 'Re: ')
    setMessage('')
    setAttachedFiles([])
    setReplyOpen(true)
  }

  const handleCloseReply = () => {
    setReplyOpen(false)
    setSelectedMessage(null)
    setRecipient('')
    setRecipientName('')
    setSubject('')
    setMessage('')
    setAttachedFiles([])
  }

  // Handle recipient input change with user lookup
  const handleRecipientChange = async (value) => {
    setRecipient(value)
    
    if (value.includes('@')) {
      // If it's an email, try to fetch user details
      const userData = await fetchUserDetails(value)
      setRecipientName(getUserDisplayName(value, userData))
    } else {
      setRecipientName('')
    }
  }

  // Send new message
  const handleSend = async () => {
    if (!recipient) {
      alert('Recipient is required')
      return
    }
    if (!message) {
      alert('Message content is required')
      return
    }
    await sendMessage({
      recipientEmail: recipient,
      subject,                   
      message,                   
    })
    setComposeOpen(false)
    setRecipient('')
    setRecipientName('')
    setSubject('')
    setMessage('')
    setAttachedFiles([])
    // Optionally refresh sent/inbox
    if (activeTab === 'sent') fetchSent()
    if (activeTab === 'inbox') fetchInbox()
  }

  // Send reply message
  const handleSendReply = async () => {
    if (!recipient) {
      alert('Recipient is required')
      return
    }
    if (!message) {
      alert('Message content is required')
      return
    }
    await sendMessage({
      recipientEmail: recipient,
      subject,                   
      message,                   
    })
    setReplyOpen(false)
    setSelectedMessage(null)
    setRecipient('')
    setRecipientName('')
    setSubject('')
    setMessage('')
    setAttachedFiles([])
    // Refresh inbox after reply
    if (activeTab === 'inbox') fetchInbox()
    fetchSent() // Also refresh sent to show the reply
  }

  const handleFileAttachment = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files)
    setAttachedFiles(prev => [...prev, ...files])
  }

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleTextFormatting = () => {
    const textarea = textareaRef.current
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = textarea.value.substring(start, end)

      if (selectedText) {
        const newText = selectedText === selectedText.toUpperCase()
          ? selectedText.toLowerCase()
          : selectedText.toUpperCase()

        const newValue =
          textarea.value.substring(0, start) +
          newText +
          textarea.value.substring(end)

        setMessage(newValue)

        setTimeout(() => {
          textarea.setSelectionRange(start, start + newText.length)
          textarea.focus()
        }, 0)
      }
    }
  }

  // Function to get sender display info
  const getSenderDisplay = (msg) => {
    const senderId = msg.senderId || msg.fromId
    const senderEmail = msg.senderEmail || msg.fromEmail || msg.sender || msg.from
    const identifier = senderId || senderEmail
    
    return getUserDisplayName(identifier)
  }

  // Function to get recipient display info
  const getRecipientDisplay = (msg) => {
    const recipientId = msg.recipientId || msg.toId
    const recipientEmail = msg.recipientEmail || msg.toEmail || msg.recipient || msg.to
    const identifier = recipientId || recipientEmail
    
    return getUserDisplayName(identifier)
  }

  // If viewing a specific message
  if (viewingMessage) {
    return (
      <>
        <MiniHeader title="Message" />
        <div className="ml-[250px] p-8 max-w-7xl">
          {/* Back button and header */}
          <div className="flex items-center mb-6">
            <Button 
              onClick={handleBackToList}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <IoArrowBack />
              Back
            </Button>
          </div>

          {/* Message detail view */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Message header */}
            <div className="border-b border-gray-200 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <Typography variant="h3" className="text-lg font-semibold mb-2">
                    {viewingMessage.subject || 'No Subject'}
                  </Typography>
                  <div className="flex flex-col space-y-1 text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="font-medium mr-2">From:</span>
                      <span>{getSenderDisplay(viewingMessage)}</span>
                      {viewingMessage.senderEmail && (
                        <span className="ml-1 text-gray-500">
                          &lt;{viewingMessage.senderEmail}&gt;
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">To:</span>
                      <span>{getRecipientDisplay(viewingMessage) || 'You'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">Date:</span>
                      <span>
                        {viewingMessage.createdAt 
                          ? new Date(viewingMessage.createdAt).toLocaleString() 
                          : 'Unknown Date'
                        }
                      </span>
                    </div>
                  </div>
                </div>
                {activeTab === 'inbox' && (
                  <Button 
                    onClick={() => handleReply(viewingMessage)}
                    className="bg-primary text-white"
                  >
                    Reply
                  </Button>
                )}
              </div>
            </div>

            {/* Message content */}
            <div className="p-6">
              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {viewingMessage.content || viewingMessage.body || viewingMessage.message || 'No message content available.'}
                </div>
              </div>

              {/* Attachments (if any) */}
              {viewingMessage.attachments && viewingMessage.attachments.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Typography className="text-sm font-medium text-gray-700 mb-2">
                    Attachments ({viewingMessage.attachments.length})
                  </Typography>
                  <div className="space-y-2">
                    {viewingMessage.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center p-2 bg-gray-50 rounded border">
                        <CgAttachment className="text-gray-500 mr-2" />
                        <span className="text-sm text-gray-700">{attachment.name || `Attachment ${index + 1}`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reply Modal */}
          {replyOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="bg-primary flex px-4 py-3 justify-between items-center rounded-t-lg">
                  <Typography variant="h4" className="text-white text-sm font-medium">
                    Reply to Message
                  </Typography>
                  <button 
                    className="text-white text-xl hover:bg-white hover:bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center"
                    onClick={handleCloseReply}
                  >
                    &times;
                  </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                  {/* To Field - Auto-filled and readonly */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <Typography className="text-sm text-gray-700 mr-2">To:</Typography>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800">{recipientName}</div>
                        <div className="text-xs text-gray-500">{recipient}</div>
                      </div>
                    </div>
                  </div>

                  {/* Subject Field - Auto-filled */}
                  <div>
                    <Typography className="text-sm text-gray-700 mb-1">Subject:</Typography>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full border-0 border-b border-gray-200 px-0 py-1 focus:outline-none focus:border-primary text-sm"
                    />
                  </div>

                  {/* Message Area */}
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows="8"
                      className="w-full border-0 resize-none focus:outline-none text-sm"
                      placeholder="Type your reply here..."
                    />
                  </div>

                  {/* File Attachments */}
                  {attachedFiles.length > 0 && (
                    <div className="border-t pt-3">
                      <Typography className="text-xs text-gray-600 mb-2">Attachments:</Typography>
                      <div className="space-y-1">
                        {attachedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                            <span className="text-gray-700 truncate">{file.name}</span>
                            <button
                              onClick={() => removeFile(index)}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 pb-4 flex justify-between items-center">
                  <Button 
                    onClick={handleSendReply} 
                    className="bg-primary text-white px-4 py-1 rounded text-sm flex items-center gap-1"
                  >
                    Send Reply
                    <IoMdArrowDropdown className="text-sm" />
                  </Button>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleTextFormatting}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                      title="Toggle text case"
                    >
                      <RxLetterCaseCapitalize className="text-lg" />
                    </button>
                    <button
                      onClick={handleFileAttachment}
                      className="text-gray-500 hover:text-gray-700 transition-colors"
                      title="Attach files"
                    >
                      <CgAttachment className="text-lg" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <MiniHeader title="Message" />
      <div className="ml-[250px] p-8 max-w-7xl">
        <div className="flex py-2 items-center justify-between">
          <div className="py-2">
            <Button onClick={handleCompose}>
              <LuPencil className="mr-2" />
              Compose
            </Button>
          </div>
          <DarkTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
          <div className="flex gap-3 py-4">
            <Typography>Unread Messages</Typography>
            <div className="bg-teal-500/10 flex items-center justify-center w-6 h-6 border border-primary rounded-full ">
              <Typography className="text-xs">{unreadCount}</Typography>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={10}
            onPageChange={setCurrentPage}
            onRefresh={handleRefresh}
            justify="end"
          />
        </div>

        {activeTab === 'inbox' && (
          <GridTable columns={columns} gridCols="grid-cols-5">
            {(inbox?.messages && inbox.messages.length > 0 ? inbox.messages : Array.isArray(inbox) ? inbox : []).length > 0 ? (
              (inbox?.messages || inbox || []).map((msg, idx) => (
                <div 
                  key={msg._id || idx} 
                  className="col-span-5 grid grid-cols-5 border-b py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleMessageClick(msg)}
                >
                  <div className="col-span-2 text-sm">{getSenderDisplay(msg)}</div>
                  <div className="col-span-2 text-sm">{msg.content || msg.body || msg.subject || '-'}</div>
                  <div className="flex justify-end text-sm">{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : '-'}</div>
                </div>
              ))
            ) : (
              <div className="col-span-5 text-center text-gray-400 py-4">No inbox messages.</div>
            )}
          </GridTable>
        )}

        {activeTab === 'sent' && (
          <GridTable columns={columns} gridCols="grid-cols-5">
            {(sent?.messages && sent.messages.length > 0 ? sent.messages : Array.isArray(sent) ? sent : []).length > 0 ? (
              (sent?.messages || sent || []).map((msg, idx) => (
                <div 
                  key={msg._id || idx} 
                  className="col-span-5 grid grid-cols-5 border-b py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleMessageClick(msg)}
                >
                  <div className="col-span-2 text-sm">{getRecipientDisplay(msg)}</div>
                  <div className="col-span-2 text-sm">{msg.content || msg.body || msg.subject || '-'}</div>
                  <div className="flex justify-end text-sm">{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : '-'}</div>
                </div>
              ))
            ) : (
              <div className="col-span-5 text-center text-gray-400 py-4">No sent messages.</div>
            )}
          </GridTable>
        )}

        {activeTab === 'drafts' && (
          <GridTable columns={columns} gridCols="grid-cols-5">
            {(drafts?.messages && drafts.messages.length > 0 ? drafts.messages : Array.isArray(drafts) ? drafts : []).length > 0 ? (
              (drafts?.messages || drafts || []).map((msg, idx) => (
                <div 
                  key={msg._id || idx} 
                  className="col-span-5 grid grid-cols-5 border-b py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => handleMessageClick(msg)}
                >
                  <div className="col-span-2 text-sm">{getRecipientDisplay(msg)}</div>
                  <div className="col-span-2 text-sm">{msg.content || msg.body || msg.subject || '-'}</div>
                  <div className="flex justify-end text-sm">{msg.createdAt ? new Date(msg.createdAt).toLocaleString() : '-'}</div>
                </div>
              ))
            ) : (
              <div className="col-span-5 text-center text-gray-400 py-4">No draft messages.</div>
            )}
          </GridTable>
        )}

        {/* Compose Modal Popup */}
        {composeOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              {/* Header */}
              <div className="bg-primary flex px-4 py-3 justify-between items-center rounded-t-lg">
                <Typography variant="h4" className="text-white text-sm font-medium">
                  New Message
                </Typography>
                <button 
                  className="text-white text-xl hover:bg-white hover:bg-opacity-20 rounded-full w-6 h-6 flex items-center justify-center"
                  onClick={handleCloseCompose}
                >
                  &times;
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* To Field */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <Typography className="text-sm text-gray-700 mr-2">To:</Typography>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={recipient}
                        onChange={(e) => handleRecipientChange(e.target.value)}
                        className="w-full border-0 border-b border-gray-200 px-0 py-1 focus:outline-none focus:border-primary text-sm"
                        placeholder="Enter recipient email..."
                      />
                      {recipientName && (
                        <div className="text-xs text-gray-500 mt-1">{recipientName}</div>
                      )}
                    </div>
                  </div>
                  <FiPlusCircle className="text-primary text-lg cursor-pointer hover:text-primary-dark ml-2" />
                </div>

                {/* Subject Field */}
                <div>
                  <Typography className="text-sm text-gray-700 mb-1">Subject:</Typography>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full border-0 border-b border-gray-200 px-0 py-1 focus:outline-none focus:border-primary text-sm"
                    placeholder=""
                  />
                </div>

                {/* Message Area */}
                <div className="flex-1">
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="8"
                    className="w-full border-0 resize-none focus:outline-none text-sm"
                    placeholder="Type your message here..."
                  />
                </div>

                {/* File Attachments */}
                {attachedFiles.length > 0 && (
                  <div className="border-t pt-3">
                    <Typography className="text-xs text-gray-600 mb-2">Attachments:</Typography>
                    <div className="space-y-1">
                      {attachedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-2 py-1 rounded text-xs">
                          <span className="text-gray-700 truncate">{file.name}</span>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 pb-4 flex justify-between items-center">
                <Button 
                  onClick={handleSend} 
                  className="bg-primary text-white px-4 py-1 rounded text-sm flex items-center gap-1"
                >
                  Send
                  <IoMdArrowDropdown className="text-sm" />
                </Button>
                
                <div className="flex gap-3">
                  <button
                    onClick={handleTextFormatting}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                    title="Toggle text case"
                  >
                    <RxLetterCaseCapitalize className="text-lg" />
                  </button>
                  <button
                    onClick={handleFileAttachment}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                    title="Attach files"
                  >
                    <CgAttachment className="text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator for user data */}
        {loadingUsers && (
          <div className="fixed bottom-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg">
            Loading user information...
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          className="hidden"
          accept="*/*"
        />
      </div>
    </>
  )
}

export default MessageList